"use client";
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../store/authstore";

type DisplayAdProps = {
  className?: string;
  style?: React.CSSProperties;
  adClient?: string;
  adSlot?: string;
};

export default function DisplayAd({ 
  className, 
  style, 
  adClient = "ca-pub-1374032065732962", 
  adSlot = "2090057461" 
}: DisplayAdProps) {
  const adRef = useRef<HTMLModElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);
  const [adLoaded, setAdLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isPremium = Boolean(user?.premium_data?.premium === "1"

  );

  // Intersection Observer for lazy loading - better Core Web Vitals
  // Do not load ads for premium users
  useEffect(() => {
    if (isPremium) return;
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasInitialized.current) {
            setIsVisible(true);
          }
        });
      },
      { rootMargin: '200px' } // Load ad 200px before visible
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isPremium]);

  useEffect(() => {
  if (isPremium) return;
  if (!isVisible || hasInitialized.current) return;
    if (typeof window === "undefined") return;

    const initAd = () => {
      if (!adRef.current || !containerRef.current) return false;
      
      if (typeof window.adsbygoogle === 'undefined') return false;
      
      const adStatus = adRef.current.getAttribute('data-adsbygoogle-status');
      if (adStatus === 'done') return true;

      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth === 0) return false;

      try {
        hasInitialized.current = true;
        
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setAdLoaded(true);
        } catch (err) {
          const error = err as Error;
          if (error?.name !== 'TagError') {
            console.warn('DisplayAd:', error?.message);
          }
        }
        return true;
      } catch {
        return true;
      }
    };

    const attempts = [200, 500, 1000, 2000, 3000];
    const timers: NodeJS.Timeout[] = [];

    attempts.forEach((delay) => {
      const timer = setTimeout(() => {
        if (initAd()) {
          timers.forEach(t => clearTimeout(t));
        }
      }, delay);
      timers.push(timer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [isVisible, isPremium]);

  // If premium user show placeholder (no ad)
  if (isPremium) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{ minWidth: '100%', display: 'block', overflow: 'hidden', textAlign: 'center', ...style }}
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={className} 
      style={{ 
        minWidth: '100%',
        display: 'block',
        overflow: 'hidden',
        textAlign: 'center',
        ...style 
      }}
    >
      <ins 
        ref={adRef}
        className="adsbygoogle"
        style={{ 
          display: 'block',
          minHeight: adLoaded ? '250px' : '50px',
          width: '100%',
          margin: '0 auto'
        }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}